import { Component } from '@theme/component';

/**
 * @typedef {Object} Refs
 * @property {HTMLElement[]} slide
 * @property {HTMLButtonElement[]} tab
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 * @extends {Component<Refs>}
 */
class CozybeadHomeV4Carousel extends Component {
  requiredRefs = ['slide', 'tab', 'previous', 'next'];
  #controller;
  #active = 0;
  #startX = null;
  #startY = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
    this.#show(this.#active);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#show(this.#active);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #bind() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('click', this.#onClick, options);
    this.addEventListener('keydown', this.#onKeyDown, options);
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true, ...options });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true, ...options });
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-cbh-prev]')) return this.#show(this.#active - 1);
    if (event.target.closest('[data-cbh-next]')) return this.#show(this.#active + 1);
    const tab = event.target.closest('[data-cbh-tab]');
    if (tab) this.#show(this.refs.tab.indexOf(tab));
  };

  #onKeyDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[data-cbh-tab]')) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.#show(this.#active + (event.key === 'ArrowRight' ? 1 : -1), true);
  };

  #onTouchStart = (event) => {
    if (event.target instanceof Element && event.target.closest('a,button')) return;
    this.#startX = event.changedTouches[0]?.clientX ?? null;
    this.#startY = event.changedTouches[0]?.clientY ?? null;
  };

  #onTouchEnd = (event) => {
    if (this.#startX === null || this.#startY === null) return;
    const dx = (event.changedTouches[0]?.clientX ?? this.#startX) - this.#startX;
    const dy = (event.changedTouches[0]?.clientY ?? this.#startY) - this.#startY;
    this.#startX = null;
    this.#startY = null;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.3) this.#show(this.#active + (dx < 0 ? 1 : -1));
  };

  #show(next, focusTab = false) {
    const { slide, tab } = this.refs;
    if (!slide?.length || !tab?.length) return;
    this.#active = (next + slide.length) % slide.length;
    slide.forEach((item, index) => { item.hidden = index !== this.#active; });
    tab.forEach((item, index) => {
      item.setAttribute('aria-selected', String(index === this.#active));
      item.tabIndex = index === this.#active ? 0 : -1;
    });
    if (focusTab) tab[this.#active]?.focus();
  }
}

if (!customElements.get('cb-home-v4-carousel')) customElements.define('cb-home-v4-carousel', CozybeadHomeV4Carousel);
